import { Group, Mesh, Object3D, Vector3 } from "three";

import { Metrics } from "../core/Metrics";
import { Maths } from "../utils/Maths";
import { Interaction } from "../core/Interaction";
import { isTouch } from "../core/Basics";
import WebGLSketch from "./WebGLSketch";

// Vectors reutilitzables per projectar la posició 3D a pantalla (evita allocations per frame)
const _wPos = new Vector3();
const _nPos = new Vector3();

export const MOUSE_SETTINGS = {
  enabled: true,
  rotX: 15,   // max tilt degrees on X axis (up/down)
  rotY: 25,   // max tilt degrees on Y axis (left/right)
  displaceX: 20,   // max horizontal repulsion offset
  displaceY: 20,   // max vertical repulsion offset
  displaceZ: 50,  // max depth push when mouse at center
};

export default class WebGLObject extends Object3D {
  dom;
  size = new Vector3();
  rot = new Vector3();
  pos = new Vector3();
  mouse = new Vector3();  // raw UV position: x=[0-1] left→right, y=[0-1] top→bottom
  mesh;
  material;
  geometry;
  hasMove = true;
  rotationGroup = new Group();    // child group: handles all lerped mouse effects

  constructor(opts = {}) {
    super();

    this.visible = opts.visible == false ? false : true;
    this.geometry = opts.geometry ?? opts.geometry;
    this.material = opts.material ?? opts.material;
    this.dom = opts.dom ?? opts.dom;
    this.hasMove = opts.hasMove !== undefined ? opts.hasMove : true;

    if (opts.size) {
      this.size.copy(opts.size);
    } else if (this.dom.getAttribute('width')) {
      this.size.x = parseInt(this.dom.getAttribute('width'));
      this.size.y = parseInt(this.dom.getAttribute('height'));
    } else {
      const { width, height } = this.dom.getBoundingClientRect();
      this.size.x = width;
      this.size.y = height;
    }

    const defaults = {
      rotFactor: 1,
      xFactor: 1,
      yFactor: 1,
      zFactor: 1,
      groupLerp: 1,   // lerp factor for rotationGroup (1 = direct, 0.06 = smooth)
    };

    this.opts = { ...defaults, ...opts };

    this.hasMouse = opts.hasMouse ?? false;
    this.dataZ = this.dom?.dataset.z ? parseFloat(this.dom.dataset.z) : 0;
    this.pos.z = this.dataZ;
    this.hasDirectMove = this.opts.xFactor + this.opts.yFactor + this.opts.zFactor + this.opts.rotFactor === 4;

    // Per-axis movement multipliers from data-movement-* attributes (default 1)
    this._movementX = this.dom?.dataset.movementX ? parseFloat(this.dom.dataset.movementX) : 1;
    this._movementY = this.dom?.dataset.movementY ? parseFloat(this.dom.dataset.movementY) : 1;
    this._movementZ = this.dom?.dataset.movementZ ? parseFloat(this.dom.dataset.movementZ) : 1;
  }

  init() {
    this.mesh = new Mesh(this.geometry, this.material);
    this.rotationGroup.add(this.mesh);
    this.add(this.rotationGroup);
    this.visible = true;

    if (this.opts.size) this.mesh.scale.set(this.opts.size.x, this.opts.size.y, this.opts.size.z);
  }

  update() {
    if (!this.visible) return;

    if (this.hasMouse && !isTouch && this.dom) {
      // Posició amb lerp (igual que el bloc no-hasMouse) perquè la posició
      // suavitzada es reflecteixi en el centre projectat.
      if (this.hasDirectMove) {
        this.position.x = this.pos.x;
        this.position.y = this.pos.y;
        this.position.z = this.pos.z;
      } else {
        this.position.x = Maths.lerp(this.position.x, this.pos.x, this.opts.xFactor);
        this.position.y = Maths.lerp(this.position.y, this.pos.y, this.opts.yFactor);
        this.position.z = Maths.lerp(this.position.z, this.pos.z, this.opts.zFactor);
      }

      // Centre del Plane en espai de pantalla: projectem la posició 3D real.
      // Usem getWorldPosition per incloure totes les transformades pare
      // (intro z/y, animacions de scroll, etc.) i no el DOM, que no es mou.
      this.getWorldPosition(_wPos);
      _nPos.copy(_wPos).project(WebGLSketch.camera);
      const cx = (_nPos.x + 1) * 0.5 * Metrics.WIDTH;
      const cy = (1 - _nPos.y) * 0.5 * Metrics.HEIGHT;

      // Mida del Plane (mesh.scale = dimensions DOM establertes per resize())
      const w = this.mesh.scale.x;
      const h = this.mesh.scale.y;

      // Mouse offset from target center (screen pixels)
      const dx = Interaction.positions.mouse.x - cx;
      const dy = Interaction.positions.mouse.y - cy;

      // Normalised to half-element-size → [-1,1] (±1 at element edges)
      // nx: screen-X  -1=left  +1=right
      // ny: screen-Y  -1=top   +1=bottom  (inverted from 3D world-Y)
      const nx = dx / (w * 0.5);
      const ny = dy / (h * 0.5);

      // Store as UV [0-1] for external reads
      this.mouse.x = (nx + 1) * 0.5;
      this.mouse.y = (ny + 1) * 0.5;

      const isOver = MOUSE_SETTINGS.enabled && Math.abs(nx) <= 1 && Math.abs(ny) <= 1;
      const lf = this.opts.groupLerp;

      const tnx = isOver ? nx : 0;
      const tny = isOver ? ny : 0;

      // Rotation
      const targetRotX = Maths.toRadians(tny * MOUSE_SETTINGS.rotX * this._movementY);
      const targetRotY = Maths.toRadians(tnx * MOUSE_SETTINGS.rotY * this._movementX);

      // Displacement — repulsion: group moves away from mouse
      const targetPosX = -tnx * MOUSE_SETTINGS.displaceX * this._movementX;
      const targetPosY = tny * MOUSE_SETTINGS.displaceY * this._movementY;

      // Z: push when mouse is at center of target
      let targetPosZ = 0;
      if (isOver) {
        const dist = Math.sqrt(tnx * tnx + tny * tny);
        const centerProx = Maths.clamp(1 - dist, 0, 1);
        targetPosZ = -centerProx * MOUSE_SETTINGS.displaceZ * this._movementZ;
      }

      // Apply lerp to rotationGroup
      this.rotationGroup.rotation.x = Maths.lerp(this.rotationGroup.rotation.x, targetRotX, lf);
      this.rotationGroup.rotation.y = Maths.lerp(this.rotationGroup.rotation.y, targetRotY, lf);
      this.rotationGroup.position.x = Maths.lerp(this.rotationGroup.position.x, targetPosX, lf);
      this.rotationGroup.position.y = Maths.lerp(this.rotationGroup.position.y, targetPosY, lf);
      this.rotationGroup.position.z = Maths.lerp(this.rotationGroup.position.z, targetPosZ, lf);

      return;
    }

    // Non-hasMouse objects: existing direct / lerp behaviour on main transform
    if (this.hasDirectMove) {
      this.position.x = this.pos.x;
      this.position.y = this.pos.y;
      this.position.z = this.pos.z;
      this.rotation.x = this.rot.x;
      this.rotation.y = this.rot.y;
      this.rotation.z = this.rot.z;
    } else {
      this.position.x = Maths.lerp(this.position.x, this.pos.x, this.opts.xFactor);
      this.position.y = Maths.lerp(this.position.y, this.pos.y, this.opts.yFactor);
      this.position.z = Maths.lerp(this.position.z, this.pos.z, this.opts.zFactor);
      this.rotation.x = Maths.lerp(this.rotation.x, this.rot.x, this.opts.rotFactor);
      this.rotation.y = Maths.lerp(this.rotation.y, this.rot.y, this.opts.rotFactor);
      this.rotation.z = Maths.lerp(this.rotation.z, this.rot.z, this.opts.rotFactor);
    }
  }

  resize(x, y, z = 1) {
    this.mesh.scale.set(x, y, z);
  }

  domPositionTo3D(__x, __y) {
    const x = -1 * Metrics.CENTER_X + __x;
    const y = Metrics.CENTER_Y - __y;
    return { x, y, z: 0 };
  }

  dispose() {
    this.rotationGroup.remove(this.mesh);
    this.remove(this.rotationGroup);
    if (this.geometry) this.geometry.dispose();
    if (this.material) this.material.dispose();
  }
}
